import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { PageHeader, RevealPasswordModal } from '../components/common';
import { ROUTES } from '../constants';
import { merchantService, ApiError } from '../services';
import {
  hasGoogleApiKey,
  searchPlaces,
  fetchPlaceDetails,
  extractFormFromPlace,
} from '../utils/googlePlaces';

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
const selectClass = inputClass + ' bg-white';

const E164_PATTERN = /^\+\d{8,15}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// BE enums for radios (release notes 4.2).
const COMPANY_TYPE_OPTIONS = [
  { value: 'P', label: 'Physical Store' },
  { value: 'O', label: 'Online Store' },
  { value: 'B', label: 'Both' },
];
const PREFERRED_OPTION_OPTIONS = [
  { value: 'G', label: 'Copy my data from Google' },
  { value: 'M', label: 'Provide data manually' },
];

const formDataFromMerchant = m => ({
  companyName:
    m?.companyName && m.companyName !== 'N/A' ? m.companyName : '',
  // Default companyType to 'P' (Physical) — most common; admin can change.
  companyType: m?.companyType || 'P',
  // Default preferredOption to 'M' (Manual) when blank, since on edit page
  // admin is usually adjusting manually rather than re-syncing from Google.
  preferredOption: m?.preferredOption || 'M',
  companyCategory: m?.sector && m.sector !== 'N/A' ? m.sector : '',
  companyCategoryId: m?.categoryId || '',
  companyAddress: m?.address && m.address !== 'N/A' ? m.address : '',
  companyLat: m?.lat !== undefined && m?.lat !== null ? String(m.lat) : '',
  companyLon: m?.lon !== undefined && m?.lon !== null ? String(m.lon) : '',
  companyCountry: m?.country && m.country !== 'N/A' ? m.country : '',
  companyCountryId: m?.countryId || '',
  companyState: m?.province || '',
  companyStateId: m?.stateId || '',
  companyCity: m?.city || '',
  companyCityId: m?.cityId || '',
  companyZip: m?.zip && m.zip !== 'N/A' ? m.zip : '',
  companyPhone: m?.phone && m.phone !== 'N/A' ? m.phone : '',
  companyEmail: m?.email && m.email !== 'N/A' ? m.email : '',
  website: m?.website || '',
  description: m?.description || '',
  // Preserved blobs — sent back unchanged to keep audit trail / Stripe link.
  companyGoogle: m?.googleID || '',
  companyGooglePlaces:
    typeof m?.companyGooglePlaces === 'string'
      ? m.companyGooglePlaces
      : m?.companyGooglePlaces
      ? JSON.stringify(m.companyGooglePlaces)
      : '',
});

// Helper: same path-tolerant list extractor as before.
const extractList = res => {
  if (Array.isArray(res?.data?.list)) return res.data.list;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  if (Array.isArray(res?.data)) return res.data;
  return [];
};

const MerchantEditBusinessDetailsPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const merchantFromState = location.state?.merchant;

  const [merchant, setMerchant] = useState(merchantFromState || null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState(false);
  const [revealModalOpen, setRevealModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  // Dropdown data
  const [sectors, setSectors] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  // Google Places state (only used when preferredOption === 'G')
  const [placeQuery, setPlaceQuery] = useState('');
  const [placePredictions, setPlacePredictions] = useState([]);
  const [placeSearching, setPlaceSearching] = useState(false);
  const [placeDropdownOpen, setPlaceDropdownOpen] = useState(false);
  const [placeError, setPlaceError] = useState(null);

  const initialFetchDoneRef = useRef(false);
  const justVerifiedRef = useRef(false);
  // Track whether we've already done the initial cascade pre-select so user-
  // triggered country changes don't accidentally re-run pre-select logic.
  const initialCascadeDoneRef = useRef(false);
  const placeSearchTimerRef = useRef(null);

  const detailHref = id
    ? ROUTES.generatePath(ROUTES.MERCHANTS_DETAIL, { id })
    : ROUTES.MERCHANTS;

  const goBackToDetail = useCallback(() => {
    if (!id) {
      navigate(ROUTES.MERCHANTS);
      return;
    }
    navigate(detailHref, { state: merchant ? { merchant } : undefined });
  }, [detailHref, id, merchant, navigate]);

  const loadMerchant = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await merchantService.getFormattedMerchantDetails(id);
      const data = response?.data;
      if (!data) {
        setError('Failed to load merchant details.');
        return;
      }
      setMerchant(data);
      const isRevealed = data.revealed === true;
      setRevealed(isRevealed);
      if (isRevealed) {
        setForm(formDataFromMerchant(data));
        // Pre-populate Google search input with current name when Google mode,
        // so the field shows the existing value instead of being empty.
        if (data.preferredOption === 'G') {
          setPlaceQuery(data.companyName || '');
        }
      } else {
        setForm(formDataFromMerchant({}));
        setRevealModalOpen(true);
      }
    } catch (err) {
      console.error('Failed to load merchant:', err);
      setError(
        err instanceof ApiError
          ? err.message
          : 'Failed to load merchant. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Fetch dropdown sources (sector + country) on mount.
  const loadDropdowns = useCallback(async () => {
    try {
      const [catRes, countryRes] = await Promise.all([
        merchantService.getCategoriesParent(),
        merchantService.getCountries(),
      ]);
      setSectors(
        extractList(catRes).map(c => ({
          id: c.id || c._id || '',
          name: c.name || c.label || '',
          slug: c.slug || '',
        }))
      );
      setCountries(
        extractList(countryRes).map(c => ({
          id: c.id || '',
          code: c.code || '',
          name: c.name || '',
        }))
      );
    } catch (err) {
      console.error('Failed to load dropdown sources:', err);
    }
  }, []);

  useEffect(() => {
    if (initialFetchDoneRef.current) return;
    initialFetchDoneRef.current = true;
    loadMerchant();
    loadDropdowns();
  }, [loadMerchant, loadDropdowns]);

  // Find country code matching the merchant's current country name. Needed
  // for state/city fetches which require ISO country code, not display name.
  const countryCodeFor = useCallback(
    name => {
      if (!name) return '';
      const match = countries.find(c => c.name === name);
      return match?.code || '';
    },
    [countries]
  );

  // Manual fetch helpers; called both on dependent change AND during the
  // initial cascade pre-select chain.
  const fetchStates = useCallback(async countryCode => {
    if (!countryCode) {
      setStates([]);
      return [];
    }
    setLoadingStates(true);
    try {
      const res = await merchantService.getStates(countryCode);
      const list = extractList(res).map(s => ({
        id: s.id || '',
        code: s.code || s.shortCode || '',
        name: s.name || '',
      }));
      setStates(list);
      return list;
    } catch (err) {
      console.error('Failed to fetch states:', err);
      setStates([]);
      return [];
    } finally {
      setLoadingStates(false);
    }
  }, []);

  const fetchCities = useCallback(async (countryCode, stateCode) => {
    if (!countryCode || !stateCode) {
      setCities([]);
      return [];
    }
    setLoadingCities(true);
    try {
      const res = await merchantService.getCities(countryCode, stateCode);
      const list = extractList(res).map(c => ({
        id: c.id || '',
        code: c.code || '',
        name: c.name || '',
      }));
      setCities(list);
      return list;
    } catch (err) {
      console.error('Failed to fetch cities:', err);
      setCities([]);
      return [];
    } finally {
      setLoadingCities(false);
    }
  }, []);

  // Once countries are loaded AND merchant data is in, do the initial cascade
  // to pre-select state & city. Guarded by ref so the user's later changes
  // don't re-trigger this chain.
  useEffect(() => {
    if (initialCascadeDoneRef.current) return;
    if (!form || countries.length === 0 || !form.companyCountry) return;
    initialCascadeDoneRef.current = true;
    const countryCode = countryCodeFor(form.companyCountry);
    if (!countryCode) return;
    (async () => {
      const stateList = await fetchStates(countryCode);
      const stateMatch = stateList.find(s => s.name === form.companyState);
      if (!stateMatch) return;
      // Persist the resolved state code/id back onto the form so cascading
      // refetches use the right value going forward.
      setForm(prev =>
        prev
          ? { ...prev, companyStateId: stateMatch.id || prev.companyStateId }
          : prev
      );
      const cityList = await fetchCities(countryCode, stateMatch.code);
      const cityMatch = cityList.find(c => c.name === form.companyCity);
      if (cityMatch) {
        setForm(prev =>
          prev
            ? { ...prev, companyCityId: cityMatch.id || prev.companyCityId }
            : prev
        );
      }
    })();
  }, [form, countries, countryCodeFor, fetchStates, fetchCities]);

  const handleChange = field => e => {
    const nextValue = e.target.value;
    setForm(prev => ({ ...prev, [field]: nextValue }));
    setFieldErrors(prev => {
      if (!prev?.[field]) return prev;
      const trimmed = String(nextValue ?? '').trim();
      if (!trimmed) return prev;
      const { [field]: _ignored, ...rest } = prev;
      return rest;
    });
    if (savedOk) setSavedOk(false);
  };

  // When admin flips Preferred Option from Manual → Google, the Company Name
  // input morphs into a Google search. Seed placeQuery with the current name
  // so the search input shows what's saved instead of going blank.
  const handlePreferredOptionChange = e => {
    const nextValue = e.target.value;
    handleChange('preferredOption')(e);
    if (nextValue === 'G' && !placeQuery && form?.companyName) {
      setPlaceQuery(form.companyName);
    }
  };

  const handleSectorChange = e => {
    const name = e.target.value;
    const match = sectors.find(s => s.name === name);
    setForm(prev => ({
      ...prev,
      companyCategory: name,
      companyCategoryId: match?.id || '',
    }));
    setFieldErrors(prev => {
      if (!prev?.companyCategory) return prev;
      const { companyCategory: _ignored, ...rest } = prev;
      return rest;
    });
    if (savedOk) setSavedOk(false);
  };

  // Country change → reset state & city, refetch state list.
  const handleCountryChange = async e => {
    const name = e.target.value;
    const match = countries.find(c => c.name === name);
    setForm(prev => ({
      ...prev,
      companyCountry: name,
      companyCountryId: match?.id || '',
      companyState: '',
      companyStateId: '',
      companyCity: '',
      companyCityId: '',
    }));
    setCities([]);
    setFieldErrors(prev => {
      const { companyCountry: _c, companyState: _s, companyCity: _ci, ...rest } =
        prev || {};
      return rest;
    });
    if (savedOk) setSavedOk(false);
    if (match?.code) await fetchStates(match.code);
    else setStates([]);
  };

  // State change → reset city, refetch city list.
  const handleStateChange = async e => {
    const name = e.target.value;
    const match = states.find(s => s.name === name);
    setForm(prev => ({
      ...prev,
      companyState: name,
      companyStateId: match?.id || '',
      companyCity: '',
      companyCityId: '',
    }));
    setFieldErrors(prev => {
      const { companyState: _s, companyCity: _c, ...rest } = prev || {};
      return rest;
    });
    if (savedOk) setSavedOk(false);
    const countryCode = countryCodeFor(form?.companyCountry);
    if (countryCode && match?.code) await fetchCities(countryCode, match.code);
    else setCities([]);
  };

  const handleCityChange = e => {
    const name = e.target.value;
    const match = cities.find(c => c.name === name);
    setForm(prev => ({
      ...prev,
      companyCity: name,
      companyCityId: match?.id || '',
    }));
    setFieldErrors(prev => {
      if (!prev?.companyCity) return prev;
      const { companyCity: _c, ...rest } = prev;
      return rest;
    });
    if (savedOk) setSavedOk(false);
  };

  // ───────── Google Places search (only relevant when preferredOption === 'G')
  const handlePlaceQueryChange = e => {
    const value = e.target.value;
    setPlaceQuery(value);
    setPlaceError(null);
    setPlaceDropdownOpen(true);
    // Debounce 300ms — matches the "feel" of onboarding without spamming Google.
    if (placeSearchTimerRef.current) clearTimeout(placeSearchTimerRef.current);
    if (!value.trim()) {
      setPlacePredictions([]);
      setPlaceSearching(false);
      return;
    }
    setPlaceSearching(true);
    placeSearchTimerRef.current = setTimeout(async () => {
      try {
        const results = await searchPlaces(value);
        setPlacePredictions(results);
      } catch (err) {
        console.error('Place search failed:', err);
        setPlacePredictions([]);
        setPlaceError('Failed to search places. Please try again.');
      } finally {
        setPlaceSearching(false);
      }
    }, 300);
  };

  const handleSelectPlace = async prediction => {
    if (!prediction?.placeId) return;
    setPlaceDropdownOpen(false);
    // Set placeholder text immediately while fetch is in flight; replaced
    // with the official displayName once place details come back.
    setPlaceQuery(prediction.label);
    setPlaceSearching(true);
    setPlaceError(null);
    try {
      const place = await fetchPlaceDetails(prediction.placeId);
      const extracted = extractFormFromPlace(place, sectors);
      // Show the canonical place name in the Company Name search input.
      if (extracted.companyName) setPlaceQuery(extracted.companyName);
      // Match extracted country name against our country list so the cascade
      // dropdowns kick off correctly (state/city refetch happens below).
      const countryMatch = countries.find(
        c => c.name === extracted.companyCountry || c.code === extracted.companyCountryCode
      );
      setForm(prev =>
        prev
          ? {
              ...prev,
              companyName: extracted.companyName || prev.companyName,
              companyAddress: extracted.companyAddress || prev.companyAddress,
              companyZip: extracted.companyZip || prev.companyZip,
              companyCountry: countryMatch?.name || extracted.companyCountry,
              companyCountryId: countryMatch?.id || '',
              companyLat: extracted.companyLat || prev.companyLat,
              companyLon: extracted.companyLon || prev.companyLon,
              companyPhone: extracted.companyPhone || prev.companyPhone,
              companyCategory:
                extracted.companyCategory || prev.companyCategory,
              companyCategoryId:
                extracted.companyCategoryId || prev.companyCategoryId,
              // Persist Google source markers for the save payload.
              companyGoogle: prediction.placeId,
              companyGooglePlaces: JSON.stringify(place),
              // Address resolution changes → invalidate cascade child values.
              companyState: '',
              companyStateId: '',
              companyCity: '',
              companyCityId: '',
            }
          : prev
      );
      setStates([]);
      setCities([]);
      if (countryMatch?.code) await fetchStates(countryMatch.code);
    } catch (err) {
      console.error('Failed to fetch place details:', err);
      setPlaceError('Failed to fetch place details. Please try again.');
    } finally {
      setPlaceSearching(false);
    }
  };

  const validate = currentForm => {
    const next = {};
    const required = [
      ['companyName', 'Business Name is required.'],
      ['companyCategory', 'Sector is required.'],
      ['companyAddress', 'Address is required.'],
      ['companyCountry', 'Country is required.'],
      ['companyCity', 'City is required.'],
      ['companyZip', 'ZIP Code is required.'],
      ['companyPhone', 'Phone Number is required.'],
      ['companyEmail', 'Email Address is required.'],
    ];
    for (const [key, msg] of required) {
      if (!String(currentForm[key] ?? '').trim()) next[key] = msg;
    }
    const phone = String(currentForm.companyPhone ?? '').trim();
    if (phone && !E164_PATTERN.test(phone)) {
      next.companyPhone = 'Phone must be in E.164 format (e.g. +393331112222).';
    }
    const email = String(currentForm.companyEmail ?? '').trim();
    if (email && !EMAIL_PATTERN.test(email)) {
      next.companyEmail = 'Email format is invalid.';
    }
    return next;
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (!id || !form) return;
    const nextErrors = validate(form);
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      setError('Please fix the highlighted fields.');
      return;
    }
    setError(null);
    setShowConfirm(true);
  };

  const handleConfirmSave = async () => {
    if (!id || !form) return;
    setShowConfirm(false);
    setSaving(true);
    setError(null);
    try {
      await merchantService.updateBusinessDetails(id, form);
      setSavedOk(true);
      setMerchant(prev =>
        prev
          ? {
              ...prev,
              companyName: form.companyName.trim(),
              companyType: form.companyType,
              preferredOption: form.preferredOption,
              sector: form.companyCategory.trim(),
              categoryId: form.companyCategoryId,
              address: form.companyAddress.trim(),
              country: form.companyCountry.trim(),
              countryId: form.companyCountryId,
              province: form.companyState.trim(),
              stateId: form.companyStateId,
              city: form.companyCity.trim(),
              cityId: form.companyCityId,
              zip: form.companyZip.trim(),
              phone: form.companyPhone.trim(),
              email: form.companyEmail.trim(),
              website: form.website.trim(),
              description: form.description.trim(),
              lat: form.companyLat,
              lon: form.companyLon,
              googleID: form.companyGoogle,
              companyGooglePlaces: form.companyGooglePlaces,
            }
          : prev
      );
    } catch (err) {
      console.error('Failed to save business details:', err);
      const code = err instanceof ApiError ? err.code : null;
      if (code === 'FeatureUnauthorized') {
        setError(
          'Your admin session has expired or lacks permission. Please re-verify and try again.'
        );
      } else if (code === 'FeatureInvalidPayload') {
        setError(
          err.message ||
            'Validation failed. Please review the highlighted fields.'
        );
      } else if (code === 'DatabaseNotFound') {
        setError('Merchant not found.');
      } else {
        setError(
          err instanceof ApiError
            ? err.message
            : 'Failed to save business details. Please try again.'
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const merchantLabel = merchant?.companyName || 'Merchant';

  if (loading && !merchant) {
    return (
      <div className='flex items-center justify-center min-h-[40vh]'>
        <div className='flex items-center space-x-2'>
          <svg
            className='animate-spin h-8 w-8 text-blue-600'
            fill='none'
            viewBox='0 0 24 24'
          >
            <circle
              className='opacity-25'
              cx='12'
              cy='12'
              r='10'
              stroke='currentColor'
              strokeWidth='4'
            />
            <path
              className='opacity-75'
              fill='currentColor'
              d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
            />
          </svg>
          <span className='text-gray-600'>Loading…</span>
        </div>
      </div>
    );
  }

  const hasFieldError = key => !!fieldErrors?.[key];
  const inputClassFor = key =>
    `${inputClass} ${
      hasFieldError(key)
        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
        : ''
    }`;
  const selectClassFor = key =>
    `${selectClass} ${
      hasFieldError(key)
        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
        : ''
    }`;

  const editable = revealed && !saving;
  const isOnlineOnly = form?.companyType === 'O';
  const isBothType = form?.companyType === 'B';
  const isGoogleSource = form?.preferredOption === 'G';
  // Figma rules (per user clarification):
  //   Company Name (Google mode + not Online) = Google search input with
  //     autocomplete dropdown + "Powered by Google" badge in header
  //   Company Name (Manual OR Online)         = plain editable input
  //   Edit Company Name                       = admin override, shown when
  //     Company Name acts as search (Google + not Online)
  //   Website Name                            = when Online OR Both
  //   Company Address                         = when not Online
  //   Longitude + Latitude                    = when Manual AND not Online
  const isGoogleSearchMode = isGoogleSource && !isOnlineOnly;
  const showEditCompanyName = isGoogleSearchMode;
  const showWebsite = isOnlineOnly || isBothType;
  const showAddress = !isOnlineOnly;
  const showLatLon = !isOnlineOnly && !isGoogleSource;

  return (
    <div className='space-y-6'>
      <PageHeader
        title='Edit Business Details'
        subtitle='Edit the billing details and make sure the data is correct'
        breadcrumbs={[
          { name: 'Home', href: ROUTES.DASHBOARD },
          { name: 'Merchants', href: ROUTES.MERCHANTS },
          { name: merchantLabel, href: detailHref, state: { merchant } },
          { name: 'Edit Business Details' },
        ]}
      />

      {savedOk && (
        <div className='bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex flex-wrap items-center justify-between gap-3'>
          <p className='text-sm text-amber-800'>Business details saved.</p>
          <button
            type='button'
            onClick={goBackToDetail}
            className='px-3 py-1.5 border border-amber-300 rounded-md text-xs font-medium text-amber-800 bg-white hover:bg-amber-50 whitespace-nowrap'
          >
            Back to Details
          </button>
        </div>
      )}

      {error && (
        <div className='bg-red-50 border border-red-200 rounded-lg p-4'>
          <p className='text-sm text-red-700'>{error}</p>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className='bg-white rounded-lg shadow-sm border border-gray-200'
      >
        <div className='px-4 md:px-6 py-5 border-b border-gray-200'>
          <h3 className='text-lg font-semibold text-gray-900'>
            {merchantLabel}
          </h3>
          <p className='text-xs text-gray-500 mt-0.5 break-all'>
            Merchant ID: {merchant?.id || '-'}
          </p>
        </div>

        <div className='p-6 space-y-6'>
          {/* Company Type radio group */}
          <fieldset>
            <legend className='block text-sm font-semibold text-gray-900 mb-2'>
              Company type
            </legend>
            <div className='flex flex-wrap gap-6'>
              {COMPANY_TYPE_OPTIONS.map(opt => (
                <label
                  key={opt.value}
                  className='inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer'
                >
                  <input
                    type='radio'
                    name='companyType'
                    value={opt.value}
                    checked={form?.companyType === opt.value}
                    onChange={handleChange('companyType')}
                    disabled={!editable}
                    className='h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500 disabled:opacity-50'
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </fieldset>

          {/* Preferred Option radio group */}
          <fieldset>
            <legend className='block text-sm font-semibold text-gray-900 mb-2'>
              Preferred Option
            </legend>
            <div className='flex flex-wrap gap-6'>
              {PREFERRED_OPTION_OPTIONS.map(opt => (
                <label
                  key={opt.value}
                  className='inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer'
                >
                  <input
                    type='radio'
                    name='preferredOption'
                    value={opt.value}
                    checked={form?.preferredOption === opt.value}
                    onChange={handlePreferredOptionChange}
                    disabled={!editable}
                    className='h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500 disabled:opacity-50'
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </fieldset>

          {/* Body fields */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5'>
            {/* Company Name. Two modes:
                  • Google + not Online → Google Places search input. Typing
                    triggers debounced autocomplete; picking a suggestion
                    auto-fills companyName + other fields and stamps the
                    companyGoogle / companyGooglePlaces payload markers.
                  • Manual OR Online    → plain editable input bound to
                    form.companyName (saved verbatim). */}
            <div>
              <div className='flex items-center justify-between mb-1'>
                <label
                  htmlFor='companyName'
                  className='block text-sm font-medium text-gray-900'
                >
                  Company Name <span className='text-red-600'>*</span>
                </label>
                {isGoogleSearchMode && (
                  <span className='text-xs text-gray-500 font-normal'>
                    Powered by{' '}
                    <span className='font-semibold text-gray-700'>Google</span>
                  </span>
                )}
              </div>
              {isGoogleSearchMode ? (
                <div className='relative'>
                  <input
                    id='companyName'
                    type='text'
                    value={placeQuery}
                    onChange={handlePlaceQueryChange}
                    onFocus={() =>
                      placePredictions.length > 0 && setPlaceDropdownOpen(true)
                    }
                    onBlur={() =>
                      // Delay so click on a suggestion registers before hide.
                      setTimeout(() => setPlaceDropdownOpen(false), 150)
                    }
                    disabled={!editable || !hasGoogleApiKey()}
                    className={inputClassFor('companyName')}
                    placeholder={
                      hasGoogleApiKey()
                        ? 'Type a business name to search Google…'
                        : 'Google Places API key not configured'
                    }
                    autoComplete='off'
                    aria-invalid={hasFieldError('companyName')}
                  />
                  {placeDropdownOpen &&
                    (placeSearching || placePredictions.length > 0) && (
                      <div className='absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-auto'>
                        {placeSearching && (
                          <div className='px-3 py-2 text-sm text-gray-500'>
                            Searching…
                          </div>
                        )}
                        {!placeSearching &&
                          placePredictions.map(p => (
                            <button
                              type='button'
                              key={p.placeId}
                              onClick={() => handleSelectPlace(p)}
                              className='w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50'
                            >
                              {p.label}
                            </button>
                          ))}
                      </div>
                    )}
                </div>
              ) : (
                <input
                  id='companyName'
                  type='text'
                  value={form?.companyName ?? ''}
                  onChange={handleChange('companyName')}
                  disabled={!editable}
                  className={inputClassFor('companyName')}
                  aria-invalid={hasFieldError('companyName')}
                />
              )}
              {hasFieldError('companyName') && (
                <p className='mt-1 text-xs text-red-600'>
                  {fieldErrors.companyName}
                </p>
              )}
              {isGoogleSearchMode && placeError && (
                <p className='mt-1 text-xs text-red-600'>{placeError}</p>
              )}
            </div>

            {/* Edit Company Name — admin override for the value actually saved
                when sourcing from Google. Bound to form.companyName so any
                change here becomes the committed value. */}
            {showEditCompanyName && (
              <div>
                <label
                  htmlFor='editCompanyName'
                  className='block text-sm font-medium text-gray-900 mb-1'
                >
                  Edit Company Name <span className='text-red-600'>*</span>
                </label>
                <input
                  id='editCompanyName'
                  type='text'
                  value={form?.companyName ?? ''}
                  onChange={handleChange('companyName')}
                  disabled={!editable}
                  className={inputClassFor('companyName')}
                  aria-invalid={hasFieldError('companyName')}
                />
              </div>
            )}

            {/* Longitude + Latitude — Manual mode and not Online. Lon first
                (left), Lat second (right) per Figma. */}
            {showLatLon && (
              <>
                <div>
                  <label
                    htmlFor='companyLon'
                    className='block text-sm font-medium text-gray-900 mb-1'
                  >
                    Longitude <span className='text-red-600'>*</span>
                  </label>
                  <input
                    id='companyLon'
                    type='text'
                    value={form?.companyLon ?? ''}
                    onChange={handleChange('companyLon')}
                    disabled={!editable}
                    className={inputClass}
                    placeholder='7.123456'
                  />
                </div>
                <div>
                  <label
                    htmlFor='companyLat'
                    className='block text-sm font-medium text-gray-900 mb-1'
                  >
                    Latitude <span className='text-red-600'>*</span>
                  </label>
                  <input
                    id='companyLat'
                    type='text'
                    value={form?.companyLat ?? ''}
                    onChange={handleChange('companyLat')}
                    disabled={!editable}
                    className={inputClass}
                    placeholder='45.123456'
                  />
                </div>
              </>
            )}

            {/* Website Name — Online or Both. */}
            {showWebsite && (
              <div>
                <label
                  htmlFor='website'
                  className='block text-sm font-medium text-gray-900 mb-1'
                >
                  Website Name
                </label>
                <input
                  id='website'
                  type='text'
                  value={form?.website ?? ''}
                  onChange={handleChange('website')}
                  disabled={!editable}
                  className={inputClassFor('website')}
                  placeholder='www.example.com'
                />
              </div>
            )}

            <div>
              <label
                htmlFor='companyCategory'
                className='block text-sm font-medium text-gray-900 mb-1'
              >
                Sector <span className='text-red-600'>*</span>
              </label>
              {sectors.length > 0 ? (
                <select
                  id='companyCategory'
                  value={form?.companyCategory ?? ''}
                  onChange={handleSectorChange}
                  disabled={!editable}
                  className={selectClassFor('companyCategory')}
                  aria-invalid={hasFieldError('companyCategory')}
                >
                  <option value=''>Select a sector…</option>
                  {sectors.map(s => (
                    <option key={s.id || s.name} value={s.name}>
                      {s.name}
                    </option>
                  ))}
                  {form?.companyCategory &&
                    !sectors.some(s => s.name === form.companyCategory) && (
                      <option value={form.companyCategory}>
                        {form.companyCategory}
                      </option>
                    )}
                </select>
              ) : (
                <input
                  id='companyCategory'
                  type='text'
                  value={form?.companyCategory ?? ''}
                  onChange={handleChange('companyCategory')}
                  disabled={!editable}
                  className={inputClassFor('companyCategory')}
                  aria-invalid={hasFieldError('companyCategory')}
                />
              )}
              {hasFieldError('companyCategory') && (
                <p className='mt-1 text-xs text-red-600'>
                  {fieldErrors.companyCategory}
                </p>
              )}
            </div>

            {/* Company Address — hidden for Online-only, preserved in state
                so BE.companyAddress requirement still satisfied with original
                value. Admin must switch type to Physical/Both to edit. */}
            {showAddress && (
              <div>
                <label
                  htmlFor='companyAddress'
                  className='block text-sm font-medium text-gray-900 mb-1'
                >
                  Company Address <span className='text-red-600'>*</span>
                </label>
                <input
                  id='companyAddress'
                  type='text'
                  value={form?.companyAddress ?? ''}
                  onChange={handleChange('companyAddress')}
                  disabled={!editable}
                  className={inputClassFor('companyAddress')}
                  autoComplete='street-address'
                  aria-invalid={hasFieldError('companyAddress')}
                />
                {hasFieldError('companyAddress') && (
                  <p className='mt-1 text-xs text-red-600'>
                    {fieldErrors.companyAddress}
                  </p>
                )}
              </div>
            )}

            <div>
              <label
                htmlFor='companyCountry'
                className='block text-sm font-medium text-gray-900 mb-1'
              >
                Country <span className='text-red-600'>*</span>
              </label>
              {countries.length > 0 ? (
                <select
                  id='companyCountry'
                  value={form?.companyCountry ?? ''}
                  onChange={handleCountryChange}
                  disabled={!editable}
                  className={selectClassFor('companyCountry')}
                  aria-invalid={hasFieldError('companyCountry')}
                >
                  <option value=''>Select a country…</option>
                  {countries.map(c => (
                    <option key={c.id || c.code || c.name} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                  {form?.companyCountry &&
                    !countries.some(c => c.name === form.companyCountry) && (
                      <option value={form.companyCountry}>
                        {form.companyCountry}
                      </option>
                    )}
                </select>
              ) : (
                <input
                  id='companyCountry'
                  type='text'
                  value={form?.companyCountry ?? ''}
                  onChange={handleChange('companyCountry')}
                  disabled={!editable}
                  className={inputClassFor('companyCountry')}
                  aria-invalid={hasFieldError('companyCountry')}
                />
              )}
              {hasFieldError('companyCountry') && (
                <p className='mt-1 text-xs text-red-600'>
                  {fieldErrors.companyCountry}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor='companyState'
                className='block text-sm font-medium text-gray-900 mb-1'
              >
                Province
              </label>
              {states.length > 0 ? (
                <select
                  id='companyState'
                  value={form?.companyState ?? ''}
                  onChange={handleStateChange}
                  disabled={!editable || loadingStates || !form?.companyCountry}
                  className={selectClass}
                >
                  <option value=''>
                    {loadingStates
                      ? 'Loading…'
                      : form?.companyCountry
                      ? 'Select a province…'
                      : 'Select country first'}
                  </option>
                  {states.map(s => (
                    <option key={s.id || s.code || s.name} value={s.name}>
                      {s.name}
                    </option>
                  ))}
                  {form?.companyState &&
                    !states.some(s => s.name === form.companyState) && (
                      <option value={form.companyState}>
                        {form.companyState}
                      </option>
                    )}
                </select>
              ) : (
                <input
                  id='companyState'
                  type='text'
                  value={form?.companyState ?? ''}
                  onChange={handleChange('companyState')}
                  disabled={!editable || loadingStates}
                  className={inputClass}
                  autoComplete='address-level1'
                  placeholder={
                    loadingStates
                      ? 'Loading…'
                      : form?.companyCountry
                      ? ''
                      : 'Select country first'
                  }
                />
              )}
            </div>

            <div>
              <label
                htmlFor='companyCity'
                className='block text-sm font-medium text-gray-900 mb-1'
              >
                City <span className='text-red-600'>*</span>
              </label>
              {cities.length > 0 ? (
                <select
                  id='companyCity'
                  value={form?.companyCity ?? ''}
                  onChange={handleCityChange}
                  disabled={!editable || loadingCities || !form?.companyState}
                  className={selectClassFor('companyCity')}
                  aria-invalid={hasFieldError('companyCity')}
                >
                  <option value=''>
                    {loadingCities
                      ? 'Loading…'
                      : form?.companyState
                      ? 'Select a city…'
                      : 'Select province first'}
                  </option>
                  {cities.map(c => (
                    <option key={c.id || c.code || c.name} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                  {form?.companyCity &&
                    !cities.some(c => c.name === form.companyCity) && (
                      <option value={form.companyCity}>
                        {form.companyCity}
                      </option>
                    )}
                </select>
              ) : (
                <input
                  id='companyCity'
                  type='text'
                  value={form?.companyCity ?? ''}
                  onChange={handleChange('companyCity')}
                  disabled={!editable || loadingCities}
                  className={inputClassFor('companyCity')}
                  autoComplete='address-level2'
                  aria-invalid={hasFieldError('companyCity')}
                  placeholder={
                    loadingCities
                      ? 'Loading…'
                      : form?.companyState
                      ? ''
                      : 'Select province first'
                  }
                />
              )}
              {hasFieldError('companyCity') && (
                <p className='mt-1 text-xs text-red-600'>
                  {fieldErrors.companyCity}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor='companyZip'
                className='block text-sm font-medium text-gray-900 mb-1'
              >
                ZIP Code <span className='text-red-600'>*</span>
              </label>
              <input
                id='companyZip'
                type='text'
                value={form?.companyZip ?? ''}
                onChange={handleChange('companyZip')}
                disabled={!editable}
                className={inputClassFor('companyZip')}
                autoComplete='postal-code'
                aria-invalid={hasFieldError('companyZip')}
              />
              {hasFieldError('companyZip') && (
                <p className='mt-1 text-xs text-red-600'>
                  {fieldErrors.companyZip}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor='companyPhone'
                className='block text-sm font-medium text-gray-900 mb-1'
              >
                Phone Number <span className='text-red-600'>*</span>
              </label>
              <input
                id='companyPhone'
                type='tel'
                value={form?.companyPhone ?? ''}
                onChange={handleChange('companyPhone')}
                disabled={!editable}
                className={inputClassFor('companyPhone')}
                placeholder='+393331112222'
                autoComplete='tel'
                aria-invalid={hasFieldError('companyPhone')}
              />
              {hasFieldError('companyPhone') && (
                <p className='mt-1 text-xs text-red-600'>
                  {fieldErrors.companyPhone}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor='companyEmail'
                className='block text-sm font-medium text-gray-900 mb-1'
              >
                Company Email Address <span className='text-red-600'>*</span>
              </label>
              <input
                id='companyEmail'
                type='email'
                value={form?.companyEmail ?? ''}
                onChange={handleChange('companyEmail')}
                disabled={!editable}
                className={inputClassFor('companyEmail')}
                autoComplete='email'
                aria-invalid={hasFieldError('companyEmail')}
              />
              {hasFieldError('companyEmail') && (
                <p className='mt-1 text-xs text-red-600'>
                  {fieldErrors.companyEmail}
                </p>
              )}
            </div>
          </div>

          <div>
            <label
              htmlFor='description'
              className='block text-sm font-medium text-gray-900 mb-1'
            >
              Business Summary
            </label>
            <textarea
              id='description'
              rows={3}
              value={form?.description ?? ''}
              onChange={handleChange('description')}
              disabled={!editable}
              className={inputClassFor('description')}
              aria-invalid={hasFieldError('description')}
            />
            {hasFieldError('description') && (
              <p className='mt-1 text-xs text-red-600'>
                {fieldErrors.description}
              </p>
            )}
          </div>

          <div className='flex flex-wrap items-center justify-end gap-3 pt-2'>
            <button
              type='button'
              onClick={goBackToDetail}
              disabled={saving}
              className='px-5 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50'
            >
              Cancel
            </button>
            <button
              type='submit'
              disabled={!editable}
              className='px-5 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </form>

      {showConfirm && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4'>
          <div className='bg-white rounded-lg shadow-xl w-full max-w-md p-6'>
            <h2 className='text-base font-semibold text-gray-900 mb-3'>
              Confirm Business Details Update
            </h2>
            <p className='text-sm text-gray-600 mb-6'>
              Please confirm the update to the merchant's business details.
              Under GDPR, you are responsible for ensuring that the data is
              accurate and up to date. This action will be logged with your
              user ID and timestamp for audit purposes.
            </p>
            <div className='flex justify-end gap-3'>
              <button
                type='button'
                onClick={() => setShowConfirm(false)}
                className='px-5 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50'
              >
                Cancel
              </button>
              <button
                type='button'
                onClick={handleConfirmSave}
                className='px-5 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700'
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      <RevealPasswordModal
        isOpen={revealModalOpen}
        onClose={() => {
          setRevealModalOpen(false);
          if (justVerifiedRef.current) {
            justVerifiedRef.current = false;
            return;
          }
          if (!revealed) goBackToDetail();
        }}
        onSuccess={async () => {
          justVerifiedRef.current = true;
          await loadMerchant();
        }}
      />
    </div>
  );
};

export default MerchantEditBusinessDetailsPage;
